import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useIsMobile } from "@/hooks/useIsMobile";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { WaitForMapReadyProgress } from "@/features/map/utils/waitForMapReady";
import type {
  MapExportMode,
  MapExportRenderOptions,
} from "@/features/map/hooks/useMapImageExport";
import type {
  MapExportOrientation,
  MapExportPaperSize,
  MapPrintOptions,
} from "@/features/map/utils/printLayout";
import {
  CUSTOM_SCALE_MAX,
  CUSTOM_SCALE_MIN,
  CUSTOM_SCALE_STEP,
  validateCustomScale,
} from "@/features/map/utils/customScaleValidation";
import {
  checkCanvasSizeExceeded,
  checkIsMobile,
} from "@/features/map/utils/canvasSizeCheck";

const SCALE_OPTIONS = [500, 1000, 1500, 2500, 5000];
const CUSTOM_SCALE_VALUE = "custom" as const;
const TITLE_MAX_LENGTH = 50;

// 絵文字や異体字セレクター付き文字を 1 文字としてカウントするため、
// UTF-16 コードユニット数ではなく書記素クラスタ単位で長さを扱う。
const titleSegmenter = new Intl.Segmenter("ja", { granularity: "grapheme" });
const countGraphemes = (value: string): number => {
  let count = 0;
  for (const _ of titleSegmenter.segment(value)) {
    count++;
  }
  return count;
};
const sliceGraphemes = (value: string, max: number): string => {
  let result = "";
  let count = 0;
  for (const { segment } of titleSegmenter.segment(value)) {
    if (count >= max) break;
    result += segment;
    count++;
  }
  return result;
};

type ScaleSelection = "" | number | typeof CUSTOM_SCALE_VALUE;

interface MapExportPanelProps {
  open: boolean;
  mapReady: boolean;
  downloading: boolean;
  onClose: () => void;
  onCreatePreview: (
    mode: MapExportMode,
    options: MapExportRenderOptions,
    highResolution?: boolean,
    force?: boolean
  ) => Promise<string | null>;
  onDownloadPng: (options: MapExportRenderOptions) => Promise<boolean>;
  onDownloadPdf: (options: MapExportRenderOptions) => Promise<boolean>;
  onApplyPrintScaleToMap: (options: MapExportRenderOptions) => boolean;
  /**
   * 印刷モードで「枠線を表示」がONのとき、現在の印刷設定を渡す。
   * OFF または画像モードのときは null を渡してクリアする。
   */
  onPrintFramePreviewChange?: (options: MapPrintOptions | null) => void;
  exportProgress: WaitForMapReadyProgress | null;
}

export const MapExportPanel: React.FC<MapExportPanelProps> = ({
  open,
  mapReady,
  downloading,
  onClose,
  onCreatePreview,
  onDownloadPng,
  onDownloadPdf,
  onApplyPrintScaleToMap,
  onPrintFramePreviewChange,
  exportProgress,
}) => {
  const isMobile = useMemo(() => checkIsMobile(), []);
  // 画面の並び（SP は地図の下にこのパネルが出る）。端末判定の isMobile とは別
  const isSpLayout = useIsMobile();
  const [mode, setMode] = useState<MapExportMode>("image");
  const [title, setTitle] = useState("");
  const [includeLayers, setIncludeLayers] = useState(true);
  const [includePins, setIncludePins] = useState(true);
  const [includeDrawnShapes, setIncludeDrawnShapes] = useState(true);
  const [includeCurrentLocation, setIncludeCurrentLocation] = useState(true);
  const [showScaleBar, setShowScaleBar] = useState(true);
  const [showPrintScaleBar, setShowPrintScaleBar] = useState(false);
  const [paperSize, setPaperSize] = useState<MapExportPaperSize>("a4");
  const [orientation, setOrientation] =
    useState<MapExportOrientation>("landscape");
  const [dpi, setDpi] = useState<number>(300);
  const [scaleSelection, setScaleSelection] = useState<ScaleSelection>("");
  const [customScaleInput, setCustomScaleInput] = useState("");
  const [showPrintFrame, setShowPrintFrame] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewDirty, setPreviewDirty] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [confirmedMode, setConfirmedMode] = useState<MapExportMode | null>(
    null
  );
  const [confirmedOptions, setConfirmedOptions] =
    useState<MapExportRenderOptions | null>(null);
  // 拡大プレビューは高解像度キャプチャを行うため数秒かかる。
  // モーダル自体は即時に開き、画像領域だけローディング表示を出すために使う。
  const [enlargedPreviewLoading, setEnlargedPreviewLoading] = useState(false);
  // プレビュー生成は非同期で、await から戻ってきた時点でアンマウント済みの
  // 可能性がある。そのときに setPreviewUrl すると Blob URL が状態にだけ
  // 残って revoke されず漏れるので、戻り時に必ずこの ref で生存確認する。
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // カスタム入力を検証し、有効な値なら数値を返す。100単位かつ最小〜最大の範囲内のみ。
  const customScaleValid = useMemo<number | null>(
    () => validateCustomScale(customScaleInput),
    [customScaleInput]
  );

  const customScaleError =
    scaleSelection === CUSTOM_SCALE_VALUE &&
    customScaleInput.trim() !== "" &&
    customScaleValid === null;

  // プレビュー・出力に渡す縮尺の分母。指定なしや無効なカスタム入力のときは "" になる。
  const scaleDenominator = useMemo<number | "">(() => {
    if (scaleSelection === CUSTOM_SCALE_VALUE) {
      return customScaleValid ?? "";
    }
    if (typeof scaleSelection === "number") {
      return scaleSelection;
    }
    return "";
  }, [scaleSelection, customScaleValid]);

  const options = useMemo<MapExportRenderOptions>(
    () => ({
      includeLayers,
      includePins,
      includeDrawnShapes,
      includeCurrentLocation,
      title,
      showScaleBar: mode === "image" ? showScaleBar : showPrintScaleBar,
      printOptions:
        mode === "print"
          ? {
              paperSize,
              orientation,
              scaleDenominator:
                scaleDenominator === "" ? undefined : scaleDenominator,
              dpi,
            }
          : undefined,
    }),
    [
      includeCurrentLocation,
      includeDrawnShapes,
      includeLayers,
      includePins,
      mode,
      orientation,
      paperSize,
      dpi,
      scaleDenominator,
      showScaleBar,
      showPrintScaleBar,
      title,
    ]
  );

  const revokePreviewUrl = (url: string | null): void => {
    if (url) {
      URL.revokeObjectURL(url);
    }
  };

  const refreshPreview = async (): Promise<void> => {
    const nextUrl = await onCreatePreview(mode, options, false, true);
    if (!nextUrl) return;
    if (!isMountedRef.current) {
      revokePreviewUrl(nextUrl);
      return;
    }
    setPreviewUrl((prev) => {
      revokePreviewUrl(prev);
      return nextUrl;
    });
    setPreviewDirty(false);
  };

  const openLatestPreview = async (): Promise<void> => {
    // モーダルを先に開いて、画像の生成は裏で進める。
    // こうすることで「クリック後しばらく真っ白」という体感をなくし、
    // モーダル内のローディング表示で進行状況を見せられる。
    setConfirmedMode(mode);
    setConfirmedOptions(options);
    setPreviewDirty(false);
    setPreviewDialogOpen(true);
    setEnlargedPreviewLoading(true);
    try {
      // 拡大プレビューは実際の保存物と同じ品質で見せたいので
      // 印刷モードでは高解像度キャプチャ (highResolution=true) を要求する。
      const nextUrl = await onCreatePreview(mode, options, true, true);
      if (!nextUrl) return;
      if (!isMountedRef.current) {
        revokePreviewUrl(nextUrl);
        return;
      }
      setPreviewUrl((prev) => {
        revokePreviewUrl(prev);
        return nextUrl;
      });
    } finally {
      if (isMountedRef.current) {
        setEnlargedPreviewLoading(false);
      }
    }
  };

  const isDpiDisabled = useCallback(
    (targetDpi: number): boolean => {
      if (mode !== "print") return false;
      return checkCanvasSizeExceeded(paperSize, orientation, targetDpi);
    },
    [mode, paperSize, orientation]
  );

  const isPaperSizeDisabled = useCallback(
    (targetPaperSize: MapExportPaperSize): boolean => {
      if (mode !== "print") return false;
      // 300dpi（最も小さい解像度）でも上限を超える場合は、その用紙サイズ自体を選択不可にする
      return checkCanvasSizeExceeded(targetPaperSize, orientation, 300);
    },
    [mode, orientation]
  );

  useEffect(() => {
    if (mode === "print" && isDpiDisabled(dpi)) {
      setDpi(300);
    }
  }, [mode, paperSize, orientation, dpi, isDpiDisabled]);

  useEffect(() => {
    if (!open) {
      setPreviewDialogOpen(false);
      setConfirmedMode(null);
      setConfirmedOptions(null);
      return;
    }
    setPreviewDirty(true);
    setConfirmedMode(null);
    setConfirmedOptions(null);
  }, [open, options]);

  useEffect(() => {
    if (!open || !mapReady || mode !== "print" || scaleDenominator === "") {
      return;
    }
    onApplyPrintScaleToMap({
      includeLayers,
      includePins,
      includeDrawnShapes,
      includeCurrentLocation,
      printOptions: {
        paperSize,
        orientation,
        scaleDenominator,
        dpi,
      },
    });
  }, [
    includeCurrentLocation,
    includeDrawnShapes,
    includeLayers,
    includePins,
    mapReady,
    mode,
    onApplyPrintScaleToMap,
    open,
    orientation,
    paperSize,
    dpi,
    scaleDenominator,
  ]);

  // 初回表示時と画像/印刷タブの切替時に自動でプレビューを生成する。
  // それ以外のオプション変更は明示的なプレビュー更新を求める。
  // downloading 中は地図コンテナを一時拡大している最中で並行キャプチャが
  // レースを起こすため、タブ切替の自動生成はスキップする。
  useEffect(() => {
    if (!open || !mapReady || downloading) return;
    void refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mapReady, mode]);

  // 印刷モードで「枠線を表示」がONかつ縮尺が指定されているときに、
  // 現在の印刷設定を親へ通知して地図上に出力範囲枠を描画してもらう。
  useEffect(() => {
    if (!onPrintFramePreviewChange) return;
    if (
      !open ||
      mode !== "print" ||
      !showPrintFrame ||
      scaleDenominator === ""
    ) {
      onPrintFramePreviewChange(null);
      return;
    }
    onPrintFramePreviewChange({
      paperSize,
      orientation,
      scaleDenominator,
      dpi,
    });
  }, [
    mode,
    onPrintFramePreviewChange,
    open,
    orientation,
    paperSize,
    dpi,
    scaleDenominator,
    showPrintFrame,
  ]);

  // アンマウント時には親側の枠線表示も確実に消す
  useEffect(() => {
    return () => {
      onPrintFramePreviewChange?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // アンマウント時に Blob URL を解放する
  useEffect(() => {
    return () => {
      setPreviewUrl((prev) => {
        revokePreviewUrl(prev);
        return null;
      });
    };
  }, []);

  const handleDownload = async (): Promise<void> => {
    if (!confirmedMode || !confirmedOptions) {
      return;
    }

    const succeeded =
      confirmedMode === "print"
        ? await onDownloadPdf(confirmedOptions)
        : await onDownloadPng(confirmedOptions);
    if (succeeded) {
      setPreviewDialogOpen(false);
      return;
    }

    // 保存失敗時（典型例はゼンリンタイルの読み込み失敗）は、表示中のプレビューが
    // 失敗時点の地図状態とずれている可能性がある。そのまま再保存してもらうと
    // 同じ古い画像を信じて再試行することになるため、同じ設定で拡大プレビューを
    // 自動的に作り直し、ユーザーには改めて保存ボタンを押してもらう。
    setEnlargedPreviewLoading(true);
    try {
      const nextUrl = await onCreatePreview(
        confirmedMode,
        confirmedOptions,
        true
      );
      if (!nextUrl) return;
      if (!isMountedRef.current) {
        revokePreviewUrl(nextUrl);
        return;
      }
      setPreviewUrl((prev) => {
        revokePreviewUrl(prev);
        return nextUrl;
      });
    } finally {
      if (isMountedRef.current) {
        setEnlargedPreviewLoading(false);
      }
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #d1d5db",
        p: 2,
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">地図出力</Typography>
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
            size="small"
            disabled={downloading}
          >
            閉じる
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {isSpLayout ? "上" : "左"}
          の地図を通常通り操作できます。範囲を調整してから、プレビュー更新または出力してください。
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={mode}
            onChange={(_event, value: MapExportMode) => {
              setMode(value);
            }}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 42,
              "& .MuiTab-root": {
                minHeight: 42,
                fontWeight: 500,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
              },
            }}
          >
            <Tab value="image" label="画像" />
            <Tab value="print" label="PDF" />
          </Tabs>
        </Box>

        <TextField
          label="タイトル"
          value={title}
          size="small"
          fullWidth
          onChange={(event) => {
            // HTML 標準の maxLength は UTF-16 コードユニット基準で絵文字を 2 文字と
            // 数えてしまうため使わず、書記素単位で onChange 時に切り詰める。
            setTitle(sliceGraphemes(event.target.value, TITLE_MAX_LENGTH));
          }}
          helperText={`${countGraphemes(title)} / ${TITLE_MAX_LENGTH}`}
        />

        <Divider />

        <Typography variant="subtitle2">含めるもの</Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeLayers}
              onChange={(event) => {
                setIncludeLayers(event.target.checked);
              }}
            />
          }
          label="レイヤー"
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includePins}
              onChange={(event) => {
                setIncludePins(event.target.checked);
              }}
            />
          }
          label="ピン"
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeDrawnShapes}
              onChange={(event) => {
                setIncludeDrawnShapes(event.target.checked);
              }}
            />
          }
          label="描画した図形"
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeCurrentLocation}
              onChange={(event) => {
                setIncludeCurrentLocation(event.target.checked);
              }}
            />
          }
          label="現在地マーカー"
        />

        {mode === "image" ? (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showScaleBar}
                onChange={(event) => {
                  setShowScaleBar(event.target.checked);
                }}
              />
            }
            label="スケールバー"
          />
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showPrintScaleBar}
                  onChange={(event) => {
                    setShowPrintScaleBar(event.target.checked);
                  }}
                />
              }
              label="スケールバー"
            />
            <Divider />
            <Typography variant="subtitle2">印刷設定</Typography>
            <TextField
              select
              label="用紙"
              size="small"
              value={paperSize}
              onChange={(event) => {
                setPaperSize(event.target.value as MapExportPaperSize);
              }}
            >
              <MenuItem value="a4" disabled={isPaperSizeDisabled("a4")}>
                A4
              </MenuItem>
              <MenuItem value="a3" disabled={isPaperSizeDisabled("a3")}>
                A3{isPaperSizeDisabled("a3") && " - メモリ制限のため選択不可"}
              </MenuItem>
            </TextField>
            <TextField
              select
              label="解像度"
              size="small"
              value={dpi}
              onChange={(event) => {
                setDpi(Number(event.target.value));
              }}
            >
              <MenuItem value={300} disabled={isDpiDisabled(300)}>
                300dpi（標準）
                {isDpiDisabled(300) && " - メモリ制限のため選択不可"}
              </MenuItem>
              <MenuItem value={600} disabled={isDpiDisabled(600)}>
                600dpi（高解像度）
                {isDpiDisabled(600) && " - メモリ制限のため選択不可"}
              </MenuItem>
              <MenuItem value={900} disabled={isDpiDisabled(900)}>
                900dpi（超高解像度）
                {isDpiDisabled(900) && " - メモリ制限のため選択不可"}
              </MenuItem>
            </TextField>
            {(isMobile || isDpiDisabled(900)) && (
              <Alert
                severity="info"
                icon={false}
                sx={{
                  py: 0.5,
                  px: 1,
                  fontSize: "0.75rem",
                  "& .MuiAlert-message": { py: 0.5 },
                }}
              >
                {isMobile
                  ? "モバイル端末ではメモリ制限のため、A3サイズの出力および高解像度（600dpi以上）の出力はご利用いただけません。"
                  : "ブラウザのメモリ制限のため、A3用紙における900dpiでの出力はご利用いただけません。"}
              </Alert>
            )}
            {(dpi === 600 || dpi === 900) && (
              <Alert
                severity="warning"
                icon={false}
                sx={{ py: 0, px: 1, "& .MuiAlert-message": { py: 1 } }}
              >
                高解像度に設定すると、出力に時間がかかります
              </Alert>
            )}
            <TextField
              select
              label="向き"
              size="small"
              value={orientation}
              onChange={(event) => {
                setOrientation(event.target.value as MapExportOrientation);
              }}
            >
              <MenuItem value="landscape">横</MenuItem>
              <MenuItem value="portrait">縦</MenuItem>
            </TextField>
            <TextField
              select
              label="縮尺"
              size="small"
              value={
                scaleSelection === ""
                  ? ""
                  : scaleSelection === CUSTOM_SCALE_VALUE
                  ? CUSTOM_SCALE_VALUE
                  : String(scaleSelection)
              }
              error={scaleSelection === ""}
              helperText={
                scaleSelection === "" ? "縮尺を指定してください" : undefined
              }
              onChange={(event) => {
                const value = event.target.value;
                if (value === "") {
                  setScaleSelection("");
                  setShowPrintFrame(false);
                } else if (value === CUSTOM_SCALE_VALUE) {
                  setScaleSelection(CUSTOM_SCALE_VALUE);
                  setShowPrintFrame(true);
                } else {
                  setScaleSelection(Number(value));
                  setShowPrintFrame(true);
                }
              }}
            >
              <MenuItem value="">指定なし</MenuItem>
              {SCALE_OPTIONS.map((scale) => (
                <MenuItem key={scale} value={String(scale)}>
                  1/{scale.toLocaleString("ja-JP")}
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM_SCALE_VALUE}>カスタム</MenuItem>
            </TextField>
            {scaleSelection === CUSTOM_SCALE_VALUE && (
              <TextField
                label="カスタム縮尺の分母"
                size="small"
                type="number"
                value={customScaleInput}
                onChange={(event) => {
                  setCustomScaleInput(event.target.value);
                }}
                inputProps={{
                  min: CUSTOM_SCALE_MIN,
                  max: CUSTOM_SCALE_MAX,
                  step: CUSTOM_SCALE_STEP,
                  inputMode: "numeric",
                }}
                error={customScaleError}
                helperText={
                  customScaleError
                    ? `${CUSTOM_SCALE_MIN.toLocaleString(
                        "ja-JP"
                      )}〜${CUSTOM_SCALE_MAX.toLocaleString(
                        "ja-JP"
                      )}の範囲で${CUSTOM_SCALE_STEP}単位で入力してください`
                    : `1/${CUSTOM_SCALE_MIN.toLocaleString(
                        "ja-JP"
                      )}〜1/${CUSTOM_SCALE_MAX.toLocaleString(
                        "ja-JP"
                      )}・${CUSTOM_SCALE_STEP}刻み`
                }
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showPrintFrame}
                  onChange={(event) => {
                    setShowPrintFrame(event.target.checked);
                  }}
                  disabled={scaleDenominator === ""}
                />
              }
              label="枠線を表示"
            />
          </>
        )}

        <Stack spacing={1}>
          <Typography variant="subtitle2">プレビュー</Typography>
          <Box sx={{ position: "relative" }}>
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="地図出力プレビュー"
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #d1d5db",
                  display: "block",
                  width: "100%",
                }}
              />
            ) : (
              <Box
                sx={{
                  alignItems: "center",
                  bgcolor: "#f9fafb",
                  border: "1px solid #d1d5db",
                  display: "flex",
                  justifyContent: "center",
                  minHeight: 160,
                  width: "100%",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  プレビューがありません
                </Typography>
              </Box>
            )}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.9)",
                border: "1px solid #d1d5db",
                borderRadius: 1,
                p: 0.25,
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <Tooltip title="プレビュー更新">
                <span>
                  <IconButton
                    size="small"
                    color={previewDirty ? "primary" : "default"}
                    disabled={downloading || !mapReady}
                    onClick={() => {
                      void refreshPreview();
                    }}
                  >
                    {downloading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="大きく確認する">
                <span>
                  <IconButton
                    size="small"
                    disabled={
                      downloading ||
                      !mapReady ||
                      !previewUrl ||
                      (mode === "print" && scaleDenominator === "")
                    }
                    onClick={() => {
                      void openLatestPreview();
                    }}
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose} disabled={downloading}>
            終了
          </Button>
          <Button
            variant="contained"
            startIcon={
              downloading ? (
                <CircularProgress size={16} />
              ) : mode === "print" ? (
                <PictureAsPdfIcon />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={
              downloading ||
              !mapReady ||
              (mode === "print" && scaleDenominator === "")
            }
            onClick={() => {
              void openLatestPreview();
            }}
          >
            {mode === "print" ? "PDF保存" : "画像保存"}
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={previewDialogOpen}
        onClose={() => {
          if (!downloading) {
            setPreviewDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          出力プレビュー
          <IconButton
            onClick={() => {
              setPreviewDialogOpen(false);
            }}
            aria-label="閉じる"
            size="small"
            disabled={downloading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: "#f3f4f6",
            position: "relative",
            minHeight: 360,
          }}
        >
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt="地図出力プレビュー"
              sx={{
                bgcolor: "#fff",
                border: "1px solid #d1d5db",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
                display: "block",
                margin: "0 auto",
                maxHeight: "calc(100vh - 220px)",
                maxWidth: "100%",
                objectFit: "contain",
                // ロード中は前回のプレビューを薄く下敷きにする。
                opacity: enlargedPreviewLoading ? 0.4 : 1,
                transition: "opacity 0.2s",
              }}
            />
          )}
          {(enlargedPreviewLoading || downloading) && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                pointerEvents: "none",
                bgcolor: "rgba(243, 244, 246, 0.8)",
                zIndex: 10,
              }}
            >
              <CircularProgress size={40} thickness={4} />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
              >
                {downloading
                  ? "保存用データを生成中..."
                  : "出力プレビューを生成中..."}
              </Typography>
              {exportProgress && (
                <Box sx={{ width: "260px", mt: 1, px: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    align="center"
                    sx={{ mb: 0.75, fontWeight: 500 }}
                  >
                    {exportProgress.totalTiles > 0 &&
                    exportProgress.loadedTiles < exportProgress.totalTiles
                      ? `地図タイルを読み込み中... (${exportProgress.loadedTiles}/${exportProgress.totalTiles})`
                      : exportProgress.loadingOverlay
                      ? "高解像度地図を読み込み中..."
                      : exportProgress.loadingLayer
                      ? "レイヤーを描画中..."
                      : "描画データを準備中..."}
                  </Typography>
                  <LinearProgress
                    variant={
                      exportProgress.totalTiles > 0 &&
                      exportProgress.loadedTiles < exportProgress.totalTiles
                        ? "determinate"
                        : "indeterminate"
                    }
                    value={
                      exportProgress.totalTiles > 0
                        ? (exportProgress.loadedTiles /
                            exportProgress.totalTiles) *
                          100
                        : 0
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "rgba(0, 0, 0, 0.06)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                        background:
                          "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={() => {
              setPreviewDialogOpen(false);
            }}
            disabled={downloading}
          >
            戻る
          </Button>
          <Button
            variant="contained"
            startIcon={
              downloading ? (
                <CircularProgress size={16} />
              ) : confirmedMode === "print" ? (
                <PictureAsPdfIcon />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={
              downloading ||
              !confirmedOptions ||
              (confirmedMode === "print" &&
                !confirmedOptions.printOptions?.scaleDenominator)
            }
            onClick={() => {
              void handleDownload();
            }}
          >
            {confirmedMode === "print" ? "PDF保存" : "PNG保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
